import styles from './index.less';
import * as React from 'react';
import { observer } from 'mobx-react';
import useStore from '@src/utils/hooks/use-store';
import Page3Store from '@src/pages/page3/store';
import Page from '@src/layout-components/page';
import $$tracker from '@src/tracker';
import { GAME_OFFICIAL_WEBSITE } from '@src/constants/urls';
import HeaderActions from '@src/ui-components/header-actions';

const designImage = require('./images/page3-design-base.jpg');
const neutralSideNavImage = require('./images/page3-side-nav-neutral.png');
const neutralFirstTabImage = require('./images/page3-first-tab-neutral.png');
const sideNavImages = [
  require('./images/page3-design-side-nav-expanded-1.png'),
  require('./images/page3-design-side-nav-expanded-2.png'),
  require('./images/page3-design-side-nav-expanded-3.png'),
  require('./images/page3-design-side-nav-expanded-4.png'),
  require('./images/page3-design-side-nav-expanded-5.png')
];
const expandButtonImage = require('@src/pages/page1/images/page1-design-side-nav-expand.png');

const DESIGN_HEIGHT = 9776;
const SCROLL_THRESHOLD = 700;
const isMobileViewport = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const screenWidth = window.screen && window.screen.width;
  return (
    screenWidth < 450 ||
    screenWidth / (window.devicePixelRatio || 1) < 450 ||
    window.outerWidth < 450 ||
    window.innerWidth < 450 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};
const pageTabs = [
  { href: '#/', label: '返回首页', left: 415, width: 178 },
  { href: '#/page1', label: '铁浮城战 焕新重启', trackerTitle: '铁浮城战', left: 593, width: 166 },
  { href: '#/page2', label: '天王觉醒 再耀锋芒', trackerTitle: '天王觉醒', left: 759, width: 164 },
  { href: '#/page3', label: '珍材现世 打宝赚金', trackerTitle: '珍材现世', left: 923, width: 166 },
  { href: '#/page4', label: '十八周年 盛馈江湖', trackerTitle: '十八周年', left: 1089, width: 166 },
  { href: '#/page5', label: '周年特献 首个生态服', trackerTitle: '周年特献', left: 1255, width: 205 }
];

const trackPageTab = tab => {
  if (tab.trackerTitle) {
    $$tracker.clickMenu(tab.trackerTitle);
  }
};

const sections = [
  { label: '六合启锋', top: 1375, left: 525 },
  { label: '炼铸神装', top: 5012, left: 702 },
  { label: '竞拍分红', top: 7675, left: 876 },
  { label: '跨服拍卖', top: 8075, left: 1054 },
  { label: '宠物系统', top: 8746, left: 1232 }
];

const toHorizontalPercent = value => `${(value / 1920) * 100}%`;
const toVerticalPercent = value => `${(value / DESIGN_HEIGHT) * 100}%`;

const Page3 = () => {
  const store = useStore(() => new Page3Store());
  const sectionRefs = React.useRef([]);
  const cmsContentRef = React.useRef(null);
  const [activeSection, setActiveSection] = React.useState(-1);
  const [isMobile, setIsMobile] = React.useState(isMobileViewport);
  const [isPastThreshold, setIsPastThreshold] = React.useState(false);
  const [isSideNavCollapsed, setIsSideNavCollapsed] = React.useState(false);

  const getSectionTargets = React.useCallback(
    () => {
      if (store.contentHtml && cmsContentRef.current) {
        // Match stable CMS chapter IDs so a missing anchor cannot shift later tabs.
        return sections.map((section, index) => cmsContentRef.current.querySelector(`a[id="${index + 1}"], a[name="${index + 1}"]`));
      }
      return sectionRefs.current;
    },
    [store.contentHtml]
  );

  React.useEffect(
    () => {
      const onScroll = () => {
        setIsPastThreshold(window.pageYOffset > SCROLL_THRESHOLD);

        // Loading images can temporarily shift anchors; the hero must remain unselected.
        if (window.pageYOffset <= 0) {
          setActiveSection(-1);
          return;
        }

        let nextActive = -1;
        // A short final CMS section cannot always reach the normal activation line.
        const isAtBottom = window.pageYOffset + window.innerHeight >= document.documentElement.scrollHeight - 2;
        getSectionTargets().forEach((section, index) => {
          if (!section) return;
          const top = section.getBoundingClientRect().top;
          if (top <= window.innerHeight * 0.25 || (isAtBottom && top < window.innerHeight)) nextActive = index;
        });
        setActiveSection(nextActive);
      };

      onScroll();
      const onResize = () => setIsMobile(isMobileViewport());
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
      };
    },
    [getSectionTargets]
  );

  const setSectionRef = index => node => {
    sectionRefs.current[index] = node;
  };

  const scrollToSection = index => {
    const section = getSectionTargets()[index];
    if (!section) return;
    // Scroll only the document; scrollIntoView also moves the overflow-hidden layout.
    const top = section.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const showSideNav = isMobile || (isPastThreshold && !isSideNavCollapsed);
  const showExpandButton = !isMobile && isPastThreshold && isSideNavCollapsed;

  return (
    <Page className={styles.root}>
      <main className={styles.designCanvas}>
        <div className={styles.designBackdrop}>
          <HeaderActions onWebsiteClick={() => $$tracker.clickGoWebsite()} onDownloadClick={() => $$tracker.clickDownload()} />
          <a
            className={styles.officialLogo}
            href={GAME_OFFICIAL_WEBSITE}
            aria-label="剑侠世界官网"
            onClick={() => $$tracker.clickGoWebsite()}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles.srOnly}>剑侠世界官网</span>
          </a>
          <img className={styles.designImage} src={designImage} alt="珍材现世打宝赚金活动资料" />
          {activeSection !== 0 ? (
            <img
              className={styles.neutralFirstTab}
              src={neutralFirstTabImage}
              alt=""
              aria-hidden="true"
              style={{ left: toHorizontalPercent(468), top: toVerticalPercent(1112), width: toHorizontalPercent(281) }}
            />
          ) : null}

          <nav className={styles.pageNav} aria-label="资料片页面导航">
            {pageTabs.map(tab => (
              <a
                href={tab.href}
                key={tab.href}
                style={{ left: toHorizontalPercent(tab.left), width: toHorizontalPercent(tab.width) }}
                aria-current={tab.href === '#/page3' ? 'page' : undefined}
                onClick={() => trackPageTab(tab)}
              >
                <span className={styles.srOnly}>{tab.label}</span>
              </a>
            ))}
          </nav>

          <nav className={styles.sectionNav} aria-label="内容章节">
            {sections.map((section, index) => (
              <button type="button" key={section.label} style={{ left: toHorizontalPercent(section.left) }} onClick={() => scrollToSection(index)}>
                <span className={styles.srOnly}>{section.label}</span>
              </button>
            ))}
          </nav>

          {sections.map((section, index) => (
            <span
              className={styles.sectionAnchor}
              key={section.label}
              ref={setSectionRef(index)}
              style={{ top: toVerticalPercent(section.top) }}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Official CMS HTML is trusted first-party publisher content. */}
        {store.contentHtml ? <section ref={cmsContentRef} className={styles.cmsContent} dangerouslySetInnerHTML={{ __html: store.contentHtml }} /> : null}
      </main>

      <aside className={`${styles.sideNav} ${showSideNav ? styles.isVisible : styles.isHidden}`} aria-label="页内导航" aria-hidden={!showSideNav}>
        <img src={activeSection < 0 ? neutralSideNavImage : sideNavImages[activeSection]} alt="" aria-hidden="true" />
        <button type="button" className={styles.collapseSideNav} onClick={() => !isMobile && setIsSideNavCollapsed(true)} tabIndex={showSideNav ? 0 : -1}>
          <span className={styles.srOnly}>收起菜单</span>
        </button>
        {sections.map((section, index) => (
          <button
            type="button"
            className={styles[`sideSection${index + 1}`]}
            key={section.label}
            onClick={() => scrollToSection(index)}
            tabIndex={showSideNav ? 0 : -1}
          >
            <span className={styles.srOnly}>{section.label}</span>
          </button>
        ))}
        <button type="button" className={styles.backTop} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} tabIndex={showSideNav ? 0 : -1}>
          <span className={styles.srOnly}>返回顶部</span>
        </button>
        <a className={styles.backHome} href="#/" tabIndex={showSideNav ? 0 : -1}>
          <span className={styles.srOnly}>返回首页</span>
        </a>
      </aside>

      <button
        type="button"
        className={`${styles.expandSideNav} ${showExpandButton ? styles.isVisible : styles.isHidden}`}
        onClick={() => setIsSideNavCollapsed(false)}
        tabIndex={showExpandButton ? 0 : -1}
        aria-hidden={!showExpandButton}
      >
        <img src={expandButtonImage} alt="" aria-hidden="true" />
        <span className={styles.srOnly}>展开菜单</span>
      </button>
    </Page>
  );
};

export default observer(Page3);
